// Firestore persistence — mirrors agreements/{id}/planner/schedule for Parenting Time Planner.
// Path: agreements/{agreementId}/holidaySchedule/draft

import firebase from 'firebase/app';
import { agreementsCollection, auth } from '@/firebase';
import store from '@/store';
import { buildInitialHolidays, SEED_HOLIDAYS } from './seed/holidays.js';

const SEED_BY_ID = Object.fromEntries(SEED_HOLIDAYS.map((h) => [h.id, h]));

const LEGACY_STORAGE_PREFIX = 'holiday-schedule';

const pendingSaves = new Map();

function legacyStorageKey(caseId) {
	return `${LEGACY_STORAGE_PREFIX}:${caseId || 'default'}`;
}

function draftRef(agreementId) {
	return agreementsCollection
		.doc(agreementId)
		.collection('holidaySchedule')
		.doc('draft');
}

function isValidCaseId(caseId) {
	return typeof caseId === 'string' && caseId.length > 0 && caseId !== 'default';
}

function serializeHolidays(holidays) {
	return JSON.parse(JSON.stringify(holidays));
}

function waitForAuth(timeoutMs = 15000) {
	if (auth.currentUser) {
		return Promise.resolve(auth.currentUser);
	}
	return new Promise((resolve, reject) => {
		const timer = setTimeout(() => {
			unsubscribe();
			reject(new Error('auth timeout'));
		}, timeoutMs);
		const unsubscribe = auth.onAuthStateChanged((user) => {
			if (user) {
				clearTimeout(timer);
				unsubscribe();
				resolve(user);
			}
		});
	});
}

function beginSaving() {
	if (store && store.commit) {
		store.commit('setSaving', true);
	}
}

function endSaving() {
	if (store && store.commit) {
		setTimeout(() => store.commit('setSaving', false), 500);
	}
}

async function saveToFirestore(caseId, holidays) {
	if (!isValidCaseId(caseId)) {
		console.warn('[holiday-schedule] skipped save — missing agreement id');
		return;
	}

	await waitForAuth();
	beginSaving();
	try {
		const uid = auth.currentUser ? auth.currentUser.uid : null;
		await draftRef(caseId).set(
			{
				holidays: serializeHolidays(holidays),
				updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
				updatedBy: uid,
			},
			{ merge: true }
		);
	} catch (e) {
		console.error('[holiday-schedule] Firestore save failed:', e);
		throw e;
	} finally {
		endSaving();
	}
}

// Migrate legacy state to the current shape. Done on load so users keep their tweaks.
export function migrate(holidays) {
	return holidays.map((h) => {
		let next = h;
		if (next?.rule?.kind === 'agreement-required') {
			next = { ...next, rule: { kind: 'unset' } };
		}
		if (next?.rule?.kind === 'noon-to-noon-alternate') {
			next = {
				...next,
				rule: {
					kind: 'paired-day-split',
					evenParent: next.rule.evenParent || 'A',
					splitTime: '12:00',
				},
			};
		}
		if (next.longWeekendType === undefined) {
			next = {
				...next,
				longWeekendType: SEED_BY_ID[next.id]?.longWeekendType ?? null,
			};
		}
		if (next.whyThisMatters === undefined) {
			next = { ...next, whyThisMatters: '' };
		}
		if (next.flaggedForDiscussion === undefined) {
			next = { ...next, flaggedForDiscussion: false };
		}
		if (next.timesByAgreement === undefined) {
			next = { ...next, timesByAgreement: false };
		}
		if (next.discussionNote === undefined) {
			next = { ...next, discussionNote: null };
		}
		if (next.customName === undefined) {
			next = { ...next, customName: false };
		}
		if (typeof next.discussionNote === 'string') {
			const text = next.discussionNote;
			next = {
				...next,
				discussionNote:
					text && text.trim().length > 0
						? { text, authorName: '', updatedAt: null }
						: null,
			};
		}
		return next;
	});
}

async function loadFromFirestore(caseId) {
	const snap = await draftRef(caseId).get();
	if (!snap.exists) return null;
	const { holidays } = snap.data();
	if (!Array.isArray(holidays) || !holidays.length) return null;
	return migrate(holidays);
}

/** One-time upload of pre-integration localStorage data into Firestore. */
async function migrateLegacyLocalStorage(caseId) {
	try {
		const raw = localStorage.getItem(legacyStorageKey(caseId));
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed) || !parsed.length) return null;
		const migrated = migrate(parsed);
		await saveToFirestore(caseId, migrated);
		localStorage.removeItem(legacyStorageKey(caseId));
		return migrated;
	} catch (e) {
		console.warn('[holiday-schedule] legacy localStorage migration failed:', e);
		return null;
	}
}

async function loadOnce(caseId, partyAName, partyBName, jurisdiction) {
	const seed = () => buildInitialHolidays(partyAName, partyBName, jurisdiction);

	if (!isValidCaseId(caseId)) {
		return seed();
	}

	await waitForAuth();

	const fromFirestore = await loadFromFirestore(caseId);
	if (fromFirestore) return fromFirestore;

	const legacy = await migrateLegacyLocalStorage(caseId);
	if (legacy) return legacy;

	const fresh = seed();
	await saveToFirestore(caseId, fresh);
	return fresh;
}

export async function load(caseId, partyAName, partyBName, jurisdiction) {
	const seed = () => buildInitialHolidays(partyAName, partyBName, jurisdiction);

	if (!isValidCaseId(caseId)) {
		return seed();
	}

	try {
		return await loadOnce(caseId, partyAName, partyBName, jurisdiction);
	} catch (e) {
		console.error('[holiday-schedule] Firestore load failed, retrying once:', e);
		try {
			await new Promise((r) => setTimeout(r, 750));
			return await loadOnce(caseId, partyAName, partyBName, jurisdiction);
		} catch (retryError) {
			console.error('[holiday-schedule] Firestore load retry failed:', retryError);
			return seed();
		}
	}
}

/** Write to Firestore immediately (same pattern as planner schedule saves). */
export function save(caseId, holidays) {
	if (!isValidCaseId(caseId)) return Promise.resolve();

	const payload = serializeHolidays(holidays);
	const prev = pendingSaves.get(caseId) || Promise.resolve();
	const next = prev
		.then(() => saveToFirestore(caseId, payload))
		.catch((e) => {
			console.error('[holiday-schedule] queued save failed:', e);
		});
	pendingSaves.set(caseId, next);
	return next;
}

/** Wait for any in-flight save — call before page unload or unmount. */
export async function flushSave(caseId) {
	if (!isValidCaseId(caseId)) return;
	const pending = pendingSaves.get(caseId);
	if (pending) await pending;
}

export async function clear(caseId, partyAName, partyBName, jurisdiction) {
	if (isValidCaseId(caseId)) {
		await flushSave(caseId);
		pendingSaves.delete(caseId);
	}

	if (!isValidCaseId(caseId)) {
		return buildInitialHolidays(partyAName, partyBName, jurisdiction);
	}

	try {
		await waitForAuth();
		beginSaving();
		await draftRef(caseId).delete();
		endSaving();
	} catch (e) {
		console.error('[holiday-schedule] Firestore clear failed:', e);
		endSaving();
	}

	return load(caseId, partyAName, partyBName, jurisdiction);
}
